import { PaymentStatus } from './cashering.model';

// ===== Enums =====

export enum PaymentType {
  MAIL_IN = 'MAIL_IN',
  WALK_IN = 'WALK_IN'
}

export enum PaymentMethodType {
  CASH = 'CASH',
  CHECK = 'CHECK',
  DEBIT_CARD = 'DEBIT_CARD',
  CREDIT_CARD = 'CREDIT_CARD'
}

export enum CardType {
  VISA = 'VISA',
  MASTERCARD = 'MASTERCARD',
  AMEX = 'AMEX',
  DISCOVER = 'DISCOVER'
}

// ===== Payment Method DTO =====

export interface PaymentMethodDto {
  id?: number;
  type: PaymentMethodType;
  amount: number;
  checkNumber?: string;
  cardLastFour?: string;
  cardType?: CardType;
  cardFeePercentage?: number;
  cardFeeAmount?: number;
  gatewayTransactionId?: string;
  createdAt?: string;
}

// ===== Payment Request DTO =====

export interface PaymentRequestDto {
  accountId: number;
  paymentType: PaymentType;
  returnIds: number[];
  paymentMethods: PaymentMethodDto[];
  officeId?: string;
  cashierUserId?: string;
  /** + over kept instead of change, - short accepted (within payment thresholds). */
  overShortAdjustment?: number;
}

// ===== Payment DTO =====

export interface PaymentDto {
  id: number;
  receiptNumber: string;
  setNumber: string;
  accountId: number;
  accountName: string;
  customerId: string;
  paymentType: PaymentType;
  status: PaymentStatus;
  paymentMethods: PaymentMethodDto[];
  returnIds: number[];
  totalAmountDue: number;
  totalPayment: number;
  overShortAmount: number;
  officeId: string;
  officeName: string;
  cashierUserId: string;
  cashierName: string;
  paymentDate: string;
  createdAt: string;
  updatedAt: string;
}

// ===== Receipt Property DTO =====

export interface ReceiptPropertyDto {
  address: string;
  grossRentalReceipts: number;
  exemptions: number;
  taxes: number;
}

// ===== Receipt DTO =====

export interface ReceiptDto {
  // Header Information
  receiptNumber: string;
  setNumber: string;
  receiptDate: string;

  // Account Information
  accountHolderName: string;
  payerName?: string;
  customerId: string;

  // Return Information
  period: string;
  returnType: string;
  frequency: string;
  returnStatus: string;

  // Property List
  properties: ReceiptPropertyDto[];

  // Financial Details
  grossRentalReceipts: number;
  totalExemptions: number;
  taxableRentalReceipts: number;
  taxRate: number;
  totalTaxes: number;
  totalFees: number;
  lateFee?: number;       // totalFees split by type
  lateInterest?: number;
  nsfFee?: number;
  totalAllowances: number;
  totalAmountDue: number;

  // Payment Details
  paymentType: PaymentType;
  paymentMethods: PaymentMethodDto[];
  totalPayment: number;
  overShortAmount: number;

  // Office Information
  officeId: string;
  officeName: string;
  officeAddress?: string;

  // Cashier Information
  cashierName: string;
  printedAt: string;

  // Contact Information
  taxCollectorName?: string;
  taxCollectorPhone?: string;
  taxCollectorEmail?: string;
  taxCollectorWebsite?: string;
}

// ===== Basket Item DTO =====

export interface BasketItemDto {
  returnId: number;
  period: string;
  type: string;
  dueDate: string;
  amountDue: number;
  addedAt: string;
}

// ===== Basket DTO =====

export interface BasketDto {
  id: number;
  accountId: number;
  accountName: string;
  customerId: string;
  paymentType: PaymentType;
  items: BasketItemDto[];
  totalAmountDue: number;
  totalPayment: number;
  totalCardFees: number;
  totalWithFees: number;
  officeId?: string;
  officeName?: string;
  cashierUserId: string;
  createdAt: string;
  updatedAt: string;
}

// ===== Legacy models for backward compatibility =====

export interface PaymentTransactionModel {
  customerId: number;
  customerName: string;
  returns: number[];
  paymentType: PaymentType;
  paymentMethods: PaymentMethodModel[];
  totalAmountDue: number;
  totalPayment: number;
  remainingAmount: number;
  overShortAmount: number;
}

export interface PaymentMethodModel {
  id?: string;
  type: PaymentMethodType;
  amount: number;
  checkNumber?: string;
  cardLastFour?: string;
  cardType?: CardType;
  cardFeePercentage?: number;
  cardFeeAmount?: number;
  gatewayTransactionId?: string;
}

export interface ReceiptModel {
  receiptNumber: string;
  setNumber: string;
  receiptDate: string;
  accountHolderName: string;
  customerId: number;
  returnPeriod: string;
  returnType: string;
  returnFrequency: string;
  returnStatus: string;
  properties: string[];
  grossRentalReceipts: number;
  exemptions: number;
  taxableRentalReceipts: number;
  totalTaxes: number;
  onlineCollectionAllowance: number;
  credit: number;
  lateFee: number;
  lateInterestFee: number;
  nsfFee: number;
  attorneyFee: number;
  lienFee: number;
  totalAmountDue: number;
  paymentMethods: PaymentMethodModel[];
  totalPaid: number;
  office: string;
  cashier: string;
  printedDate: string;
}
